import React, { useMemo, useState, useEffect } from "react";
import { BarChart3, BadgeDollarSign, CreditCard, Heart, Mail, Search, ShoppingCart, ShoppingBag, Eye } from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import B2CHeader from "./B2CHeader";
import { Badge, Card, FieldBase, PrimaryButton, SecondaryButton, cx } from "./b2cUi";

// Mock data - replace with actual API call
const MOCK_WISHLIST_DATA = [
  {
    id: 1,
    customerName: "John Doe",
    customerEmail: "john@example.com",
    productName: "Wireless Bluetooth Headphones",
    productId: "PROD-001",
    productImage: "https://via.placeholder.com/60x60",
    price: 89.99,
    addedDate: "2024-01-15",
    category: "Electronics",
  },
  {
    id: 2,
    customerName: "Jane Smith",
    customerEmail: "jane@example.com",
    productName: "Smart Watch Series 5",
    productId: "PROD-002",
    productImage: "https://via.placeholder.com/60x60",
    price: 299.99,
    addedDate: "2024-01-14",
    category: "Electronics",
  },
  {
    id: 3,
    customerName: "Mike Johnson",
    customerEmail: "mike@example.com",
    productName: "Organic Coffee Beans",
    productId: "PROD-003",
    productImage: "https://via.placeholder.com/60x60",
    price: 24.99,
    addedDate: "2024-01-13",
    category: "Food & Beverages",
  },
  {
    id: 4,
    customerName: "Sarah Wilson",
    customerEmail: "sarah@example.com",
    productName: "Yoga Mat Premium",
    productId: "PROD-004",
    productImage: "https://via.placeholder.com/60x60",
    price: 45.0,
    addedDate: "2024-01-12",
    category: "Sports & Fitness",
  },
];

const MOCK_CART_DATA = [
  {
    id: 1,
    customerName: "David Brown",
    customerEmail: "david@example.com",
    productName: "Laptop Stand Adjustable",
    productId: "PROD-005",
    productImage: "https://via.placeholder.com/60x60",
    price: 39.99,
    quantity: 2,
    addedDate: "2024-01-15",
    category: "Office Supplies",
  },
  {
    id: 2,
    customerName: "Emily Davis",
    customerEmail: "emily@example.com",
    productName: "Wireless Mouse",
    productId: "PROD-006",
    productImage: "https://via.placeholder.com/60x60",
    price: 29.99,
    quantity: 1,
    addedDate: "2024-01-14",
    category: "Electronics",
  },
  {
    id: 3,
    customerName: "Robert Wilson",
    customerEmail: "robert@example.com",
    productName: "Desk Lamp LED",
    productId: "PROD-007",
    productImage: "https://via.placeholder.com/60x60",
    price: 59.99,
    quantity: 1,
    addedDate: "2024-01-13",
    category: "Home & Office",
  },
];

const WishlistManagement = () => {
  const [wishlistData, setWishlistData] = useState([]);
  const [cartData, setCartData] = useState([]);
  const [filteredWishlist, setFilteredWishlist] = useState([]);
  const [filteredCart, setFilteredCart] = useState([]);
  const [wishlistFilter, setWishlistFilter] = useState("");
  const [cartFilter, setCartFilter] = useState("");
  const [activeTab, setActiveTab] = useState("wishlist");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setWishlistData(MOCK_WISHLIST_DATA);
      setCartData(MOCK_CART_DATA);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    let filtered = wishlistData;

    if (wishlistFilter) {
      filtered = filtered.filter(item =>
        item.productName.toLowerCase().includes(wishlistFilter.toLowerCase()) ||
        item.customerName.toLowerCase().includes(wishlistFilter.toLowerCase()) ||
        item.customerEmail.toLowerCase().includes(wishlistFilter.toLowerCase()) ||
        item.category.toLowerCase().includes(wishlistFilter.toLowerCase())
      );
    }

    setFilteredWishlist(filtered);
  }, [wishlistData, wishlistFilter]);

  useEffect(() => {
    let filtered = cartData;

    if (cartFilter) {
      filtered = filtered.filter(item =>
        item.productName.toLowerCase().includes(cartFilter.toLowerCase()) ||
        item.customerName.toLowerCase().includes(cartFilter.toLowerCase()) ||
        item.customerEmail.toLowerCase().includes(cartFilter.toLowerCase()) ||
        item.category.toLowerCase().includes(cartFilter.toLowerCase())
      );
    }

    setFilteredCart(filtered);
  }, [cartData, cartFilter]);

  const getTotalWishlistValue = () => {
    return wishlistData.reduce((sum, item) => sum + item.price, 0);
  };

  const getTotalCartValue = () => {
    return cartData.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getTotalCartItems = () => {
    return cartData.reduce((sum, item) => sum + item.quantity, 0);
  };

  const wishlistTotal = useMemo(() => getTotalWishlistValue(), [wishlistData]);
  const cartTotal = useMemo(() => getTotalCartValue(), [cartData]);
  const cartItems = useMemo(() => getTotalCartItems(), [cartData]);

  const activeRows = activeTab === "wishlist" ? filteredWishlist : filteredCart;

  return (
    <AdminPageShell
      title="Wishlist & Cart"
      description="Monitor customer wishlists and shopping cart activities. (Mock data)"
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge>
            <Heart className="h-4 w-4" />
            {wishlistData.length} wishlist
          </Badge>
          <Badge tone="blue">
            <ShoppingCart className="h-4 w-4" />
            {cartItems} cart items
          </Badge>
          <Badge tone="gray">
            <BadgeDollarSign className="h-4 w-4" />
            ${wishlistTotal.toFixed(2)} wishlist value
          </Badge>
          <Badge tone="purple">
            <CreditCard className="h-4 w-4" />
            ${cartTotal.toFixed(2)} cart value
          </Badge>
        </div>
      }
    >
      <div className="space-y-6">
        <B2CHeader />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-rose-200 bg-rose-50 flex items-center justify-center">
                <Heart className="h-5 w-5 text-rose-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Wishlist Items</p>
                <p className="text-xl font-black text-slate-900">{wishlistData.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-sky-200 bg-sky-50 flex items-center justify-center">
                <ShoppingCart className="h-5 w-5 text-sky-900" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Cart Items</p>
                <p className="text-xl font-black text-slate-900">{cartItems}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-white flex items-center justify-center">
                <BadgeDollarSign className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Wishlist Value</p>
                <p className="text-xl font-black text-slate-900">${wishlistTotal.toFixed(2)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-violet-200 bg-violet-50 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-violet-900" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Cart Value</p>
                <p className="text-xl font-black text-slate-900">${cartTotal.toFixed(2)}</p>
              </div>
            </div>
          </Card>
        </div>

        <Card>
          <div className="p-5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("wishlist")}
                className={cx(
                  "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-black transition-colors",
                  activeTab === "wishlist"
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                )}
              >
                <Heart className="h-4 w-4" />
                Wishlist ({wishlistData.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("cart")}
                className={cx(
                  "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-black transition-colors",
                  activeTab === "cart"
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                )}
              >
                <ShoppingCart className="h-4 w-4" />
                Carts ({cartData.length})
              </button>
            </div>

            <div className="w-full md:w-[420px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder={
                    activeTab === "wishlist"
                      ? "Search wishlist by product, customer, category"
                      : "Search carts by product, customer, category"
                  }
                  className={cx(FieldBase, "pl-10")}
                  value={activeTab === "wishlist" ? wishlistFilter : cartFilter}
                  onChange={(e) =>
                    activeTab === "wishlist" ? setWishlistFilter(e.target.value) : setCartFilter(e.target.value)
                  }
                />
              </div>
            </div>
          </div>

          <div className="p-5">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
                <p className="mt-2 text-slate-600">Loading…</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Price</th>
                      {activeTab === "cart" ? (
                        <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Qty</th>
                      ) : null}
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Added</th>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {activeRows.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <img
                              className="h-10 w-10 rounded-2xl border border-slate-200 object-cover"
                              src={item.productImage}
                              alt={item.productName}
                            />
                            <div className="ml-4">
                              <div className="text-sm font-semibold text-slate-900">{item.productName}</div>
                              <div className="text-sm text-slate-600">{item.productId}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">{item.customerName}</div>
                          <div className="text-sm text-slate-600">{item.customerEmail}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge tone="gray">{item.category}</Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                          ${Number(item.price).toFixed(2)}
                        </td>
                        {activeTab === "cart" ? (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{item.quantity}</td>
                        ) : null}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {new Date(item.addedDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-wrap items-center gap-2">
                            <SecondaryButton
                              type="button"
                              className="px-3 py-1.5 text-xs"
                              onClick={() => alert("View coming soon")}
                            >
                              <Eye className="h-4 w-4" />
                              View
                            </SecondaryButton>
                            <SecondaryButton
                              type="button"
                              className="px-3 py-1.5 text-xs"
                              onClick={() => alert("Contact coming soon")}
                            >
                              <Mail className="h-4 w-4" />
                              Contact
                            </SecondaryButton>
                            {activeTab === "wishlist" ? (
                              <SecondaryButton
                                type="button"
                                className="px-3 py-1.5 text-xs"
                                onClick={() => alert("Add to cart coming soon")}
                              >
                                <ShoppingBag className="h-4 w-4" />
                                Add to Cart
                              </SecondaryButton>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeRows.length === 0 && !loading ? (
              <div className="text-center py-8">
                <p className="text-slate-600">No {activeTab === "wishlist" ? "wishlist items" : "cart items"} found.</p>
              </div>
            ) : null}
          </div>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5 text-slate-700" />
              <h3 className="text-sm font-black text-slate-900">Wishlist Analytics</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Most wishlisted category</span>
                <span className="text-sm font-semibold text-slate-900">Electronics</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Avg. wishlist size</span>
                <span className="text-sm font-semibold text-slate-900">3.2 items</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Conversion rate</span>
                <span className="text-sm font-semibold text-slate-900">15.3%</span>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5 text-slate-700" />
              <h3 className="text-sm font-black text-slate-900">Cart Analytics</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Avg. cart value</span>
                <span className="text-sm font-semibold text-slate-900">
                  ${cartData.length > 0 ? (cartTotal / cartData.length).toFixed(2) : "0.00"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Abandonment rate</span>
                <span className="text-sm font-semibold text-slate-900">68.2%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Avg. items per cart</span>
                <span className="text-sm font-semibold text-slate-900">
                  {cartData.length > 0 ? (cartItems / cartData.length).toFixed(1) : "0.0"}
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-black text-slate-900 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <PrimaryButton type="button" className="w-full justify-center" onClick={() => alert("Coming soon")}
              >
                Send Wishlist Reminders
              </PrimaryButton>
              <SecondaryButton type="button" className="w-full justify-center" onClick={() => alert("Coming soon")}
              >
                Abandoned Cart Recovery
              </SecondaryButton>
              <SecondaryButton type="button" className="w-full justify-center" onClick={() => alert("Coming soon")}
              >
                Generate Reports
              </SecondaryButton>
            </div>
          </Card>
        </div>
      </div>
    </AdminPageShell>
  );
};

export default WishlistManagement;
